import { useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getTagColorClass } from "@/hooks/useUserTags";
import type { UserTag } from "@/hooks/useUserTags";

interface DeleteTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tag: UserTag | null;
  userCount: number;
  onConfirm: () => Promise<void>;
  isDeleting: boolean;
}

export function DeleteTagDialog({
  open,
  onOpenChange,
  tag,
  userCount,
  onConfirm,
  isDeleting,
}: DeleteTagDialogProps) {
  const [confirmText, setConfirmText] = useState("");

  const requiresConfirmation = userCount > 0;
  const isConfirmed = !requiresConfirmation || confirmText === tag?.name;

  const handleConfirm = async () => {
    await onConfirm();
    setConfirmText("");
  };

  if (!tag) return null;

  return (
    <AlertDialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setConfirmText(""); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Delete Tag
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>
                Are you sure you want to delete this tag?
              </p>

              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted">
                <Badge className={getTagColorClass(tag.color)}>{tag.name}</Badge>
                {tag.description && (
                  <span className="text-sm text-muted-foreground">{tag.description}</span>
                )}
              </div>

              {userCount > 0 && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <p className="text-sm text-destructive font-medium">
                    This tag is assigned to {userCount} user{userCount !== 1 ? "s" : ""}.
                    Deleting it will remove the tag from all users.
                  </p>
                </div>
              )}

              {requiresConfirmation && (
                <div className="space-y-2">
                  <Label htmlFor="confirm-delete">
                    Type <strong>{tag.name}</strong> to confirm
                  </Label>
                  <Input
                    id="confirm-delete"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Enter tag name"
                    disabled={isDeleting}
                  />
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isDeleting || !isConfirmed}
          >
            {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete Tag
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
